import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  Space,
  Button,
  Tag,
  Descriptions,
  message,
  Divider,
  Typography,
  Card,
  Input,
  Form,
  Select,
  Radio,
  Popconfirm,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  WalletOutlined,
  BankOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import DateCustom from "../../../../hook/DateCustom";
import {
  HEADERS_EXPORT_BEN,
  POST_BILL_DETAIL,
  POST_APPROVALADVANE_PAY,
  GET_LOAD_BANK,
  POST_ADVANE_PAY_DB2,
} from "../../../API/apiUrls";
import axios from "axios";
import "../../../../assets/styles/tableStyles.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ExpenseApprovalModal = ({
  open,
  close,
  dataDefault,
  funcUpdateStatus,
  nameLawyers,
}) => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const ROLE_ID = parseInt(localStorage.getItem("ROLE_ID"));
  const [convertDateThai] = DateCustom();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [billData, setBillData] = useState(null);
  const [bankData, setBankData] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);

  // ============== LOAD DATA ==============
  const loadData = useCallback(async () => {
    if (!dataDefault?.TMBILL) return;

    setLoading(true);
    try {
      const { data } = await axios.post(
        POST_BILL_DETAIL,
        { TMBILL: [dataDefault.TMBILL] },
        { headers: HEADERS_EXPORT_BEN },
      );

      if (Array.isArray(data) && data.length > 0) {
        console.log("Bill Detail Response:", data);
        setBillData(data);
      } else {
        message.warning("ไม่พบข้อมูลรายละเอียด");
        setBillData(null);
      }
    } catch (error) {
      console.error("Error loading bill detail:", error);
      message.error(`เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`);
      setBillData(null);
    } finally {
      setLoading(false);
    }
  }, [dataDefault?.TMBILL]);

  const loadBank = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(GET_LOAD_BANK, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (Array.isArray(data) && data.length > 0) {
        setBankData(data);
      } else {
        message.warning("ไม่พบข้อมูลธนาคาร");
        setBankData([]);
      }
      setLoading(false);
    } catch (error) {
      message.error(`โหลดข้อมูลธนาคารไม่สำเร็จ: ${error.message}`);
      setBankData([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && dataDefault?.TMBILL) {
      form.resetFields();
      loadData();
    }
    if (
      open &&
      dataDefault?.TMBILL &&
      dataDefault?.approvalStatus === 1 &&
      (ROLE_ID === 1 || ROLE_ID === 6)
    ) {
      loadBank();
    }
  }, [
    open,
    dataDefault?.TMBILL,
    dataDefault?.approvalStatus,
    loadData,
    loadBank,
    ROLE_ID,
    userId,
    form,
  ]);

  // ============== PREPARE TABLE DATA ==============
  const tableData = useMemo(() => {
    if (!billData || !Array.isArray(billData)) return [];

    let flatData = [];
    let rowIndex = 0;

    billData.forEach((loan) => {
      if (loan.workExpenseList && Array.isArray(loan.workExpenseList)) {
        loan.workExpenseList.forEach((expense, index) => {
          flatData.push({
            ...expense,
            CONTNO: loan.CONTNO,
            rowIndex: rowIndex++,
            isFirstInGroup: index === 0,
            groupSize: loan.workExpenseList.length,
          });
        });
      }
    });

    return flatData;
  }, [billData]);

  // ============== CALCULATE TOTAL ==============
  const totalAmount = useMemo(() => {
    return parseFloat(dataDefault?.payamt) || 0;
  }, [dataDefault]);

  // ============== TABLE COLUMNS ==============
  const columns = [
    {
      title: "สัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
      onCell: (record) => ({
        rowSpan: record.isFirstInGroup ? record.groupSize : 0,
      }),
      render: (CONTNO) => <Text strong>{CONTNO || "-"}</Text>,
    },
    {
      title: "ลำดับ",
      align: "center",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "รายการ",
      dataIndex: "description",
      key: "description",
      render: (text) => <>{text || "-"}</>,
    },
    {
      title: "จำนวนเงิน",
      dataIndex: "NETPAY",
      key: "NETPAY",
      width: 150,
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: "#1890ff", fontSize: "14px" }}>
          {currencyFormatPoint(amount)} ฿
        </Text>
      ),
    },
  ];
  // Function ตรวจสอบแถวสุดท้ายของกลุ่ม
  const isLastInGroup = (record, dataSource) => {
    const currentIndex = dataSource.findIndex((item) => item.id === record.id);
    const nextRecord = dataSource[currentIndex + 1];
    return !nextRecord || nextRecord.CONTNO !== record.CONTNO;
  };

  // เพิ่ม function สำหรับหาหมายเลขกลุ่ม
  const getGroupIndex = (record, dataSource) => {
    const uniqueContracts = [...new Set(dataSource.map((item) => item.CONTNO))];
    return uniqueContracts.indexOf(record.CONTNO);
  };

  // สร้างชุดสีสำหรับแต่ละกลุ่ม
  const getGroupColor = (groupIndex) => {
    const colors = [
      // "#ffffff", // สีขาว
      // "#f0f5ff", // น้ำเงินอ่อน
      // "#fff7e6", // ส้มอ่อน
      "#f6ffed", // เขียวอ่อน
      // "#fff0f6", // ชมพูอ่อน
      "#f9f0ff", // ม่วงอ่อน
      // "#e6fffb", // เขียวมิ้นท์อ่อน
      // "#fffbe6", // เหลืองอ่อน
    ];

    return colors[groupIndex % colors.length];
  };

  // ============== HANDLE APPROVE ==============
  const handleApprove = async (values) => {
    const remark = values.remark || "";

    Modal.confirm({
      title: "ยืนยันการอนุมัติ",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <p>
            <strong>เลขที่อ้างอิง:</strong> {dataDefault?.TMBILL}
          </p>
          <p>
            <strong>ผู้ตั้งเบิก:</strong>{" "}
            {nameLawyers?.find((item) => item.id === dataDefault?.userId)
              ?.NNAME || "-"}
          </p>
          <p>
            <strong>ยอดรวม:</strong>{" "}
            <Text strong style={{ color: "#1890ff" }}>
              {currencyFormatPoint(totalAmount)} บาท
            </Text>
          </p>
          <p>
            <strong>หมายเหตุ:</strong> {remark || "-"}
          </p>
          <Divider style={{ margin: "12px 0" }} />
          <p style={{ color: "#52c41a" }}>
            คุณต้องการอนุมัติรายการนี้ใช่หรือไม่?
          </p>
        </div>
      ),
      okText: "อนุมัติ",
      cancelText: "ยกเลิก",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        await submitApproval(1, remark);
      },
    });
  };

  // ============== HANDLE REJECT ==============
  const handleReject = async (values) => {
    const remark = values.remark;

    // if (!remark.trim()) {
    //   message.warning("กรุณาระบุหมายเหตุสำหรับการไม่อนุมัติ");
    //   return;
    // }

    Modal.confirm({
      title: "ยืนยันการไม่อนุมัติ",
      icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>
            <strong>เลขที่อ้างอิง:</strong> {dataDefault?.TMBILL}
          </p>
          <p>
            <strong>ผู้ตั้งเบิก:</strong>{" "}
            {nameLawyers?.find((item) => item.id === dataDefault?.userId)
              ?.NNAME || "-"}
          </p>
          <p>
            <strong>ยอดรวม:</strong>{" "}
            <Text strong style={{ color: "#1890ff" }}>
              {currencyFormatPoint(totalAmount)} บาท
            </Text>
          </p>
          <p>
            <strong>หมายเหตุ:</strong> {remark || "-"}
          </p>
          <Divider style={{ margin: "12px 0" }} />
          <p style={{ color: "#ff4d4f" }}>
            <ExclamationCircleOutlined />{" "}
            คุณต้องการไม่อนุมัติรายการนี้ใช่หรือไม่?
          </p>
        </div>
      ),
      okText: "ไม่อนุมัติ",
      cancelText: "ยกเลิก",
      okButtonProps: { danger: true },
      onOk: async () => {
        await submitApproval(2, remark);
      },
    });
  };

  // ============== SUBMIT APPROVAL ==============
  const submitApproval = async (approvalStatus, remark) => {
    setLoading(true);

    const payload = {
      userId: userId,
      TMBILL: dataDefault.TMBILL,
      approvalStatus: approvalStatus,
      payamtMark: remark,
    };

    console.log("Approval Payload:", payload);

    try {
      const response = await axios.post(POST_APPROVALADVANE_PAY, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (response.status === 200 || response.status === 201) {
        message.success(
          approvalStatus === 1 ? "อนุมัติสำเร็จ" : "ไม่อนุมัติสำเร็จ",
        );

        if (funcUpdateStatus) {
          funcUpdateStatus();
        }

        handleClose();
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error submitting approval:", error);
      const errorMsg = error.response?.data?.message || error.message;
      message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ============== HANDLE CLOSE ==============
  const handleClose = () => {
    close();
  };

  // ============== RENDER STATUS ==============
  const renderStatus = (status) => {
    const statusMap = {
      0: { text: "รอดำเนินการ", color: "blue" },
      1: { text: "อนุมัติเบิก", color: "green" },
      2: { text: "ไม่อนุมัติ", color: "red" },
    };

    const { text, color } = statusMap[status] || statusMap[0];
    return <Tag color={color}>{text}</Tag>;
  };

  const handleCreatePayment = async () => {
    setLoading(true);
    try {
      const payload = {
        TMBILL: dataDefault.TMBILL,
        userId: dataDefault.userId, // ทนาย
        CHKFLAG: paymentType, // 1 เงินสด 2 เงินโอน
        BANKBOOKCOD: paymentType === "1" ? "" : selectedBank.value, // รหัสแบงค์ (ถ้าเงินสดให้ "" มา)
      };
      console.log("📌 Payload ส่ง API:", payload);
      const result = await postCreatePaymentDB2(payload);

      if (result) {
        funcUpdateStatus();
      }
      close();
    } catch (err) {
      console.error("❌ Error saving:", err);
    } finally {
      setLoading(false);
    }
  };

  const postCreatePaymentDB2 = async (payload) => {
    console.log("📤 POST_DOC_STATUS with data:", payload);

    try {
      const res = await axios.post(POST_ADVANE_PAY_DB2, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }

      console.error("❌ postDocStatus error:", error);
      throw error;
    }
  };

  const handleModalCreate = () => {
    const bankOptions = (bankData || []).map((b) => ({
      value: b.BANKBOOKCOD,
      label: `${b.BANKCOD} | ${b.BOOKNAM} | ${b.BOOKNO}`,
      // เก็บ object ทั้งหมดไว้ใน data
      data: b,
    }));
    return (
      <>
        <Card
          size="small"
          title={
            <Text strong>
              วิธีการชำระเงิน <Text type="danger">*</Text>
            </Text>
          }
          style={{ marginBottom: 16, borderRadius: 8 }}
        >
          <Radio.Group
            value={paymentType}
            onChange={(e) => {
              setPaymentType(e.target.value);
              setSelectedBank(null);
            }}
            style={{ width: "100%" }}
          >
            <Space direction="horizontal" size="large">
              <Radio value="1">
                <Space>
                  <WalletOutlined />
                  เงินสด
                </Space>
              </Radio>
              <Radio value="2">
                <Space>
                  <BankOutlined />
                  โอนเงิน
                </Space>
              </Radio>
              <Radio value="3" disabled>
                <Space>
                  <FileTextOutlined />
                  เช็ค
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
          {paymentType !== "1" && (
            <div style={{ marginTop: 12 }}>
              <Text style={{ display: "block", marginBottom: 6 }}>
                เลือกธนาคาร <Text type="danger">*</Text>
              </Text>
              <Select
                placeholder="เลือกธนาคาร"
                options={bankOptions}
                value={selectedBank?.value}
                onSelect={(value, option) => setSelectedBank(option)} // ✅ ได้ { value, label, data }
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                size="large"
                loading={!bankData}
                optionRender={(
                  option, // ✅ custom render แสดง 3 บรรทัด
                ) => (
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 13 }}>
                      {option.data.data?.BANKCOD}
                    </Text>
                    <Text style={{ fontSize: 12, color: "#555" }}>
                      {option.data.data?.BOOKNAM}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      เลขที่บัญชี: {option.data.data?.BOOKNO}
                    </Text>
                  </Space>
                )}
              />
            </div>
          )}
        </Card>
      </>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
          <span style={{ fontSize: "18px", fontWeight: "bold" }}>
            รายละเอียดการเบิกค่าใช้จ่าย
          </span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={1000}
      footer={null}
      centered
      destroyOnHidden
    >
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        {/* ============== HEADER INFO ============== */}
        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: "rgba(0, 57, 96, 0.08)",
            border: "1px solid rgba(0, 57, 96, 0.15)",
          }}
        >
          <Descriptions span={2} size="small">
            <Descriptions.Item label="เลขที่อ้างอิง">
              <Tag color="blue" style={{ fontSize: "14px" }}>
                {dataDefault?.TMBILL || "-"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="ผู้ตั้งเบิก">
              <Text>
                {nameLawyers?.find((item) => item.id === dataDefault?.userId)
                  ?.NNAME || "-"}
              </Text>
            </Descriptions.Item>

            <Descriptions.Item label="วันที่ขอเบิก">
              <Text>
                {dataDefault?.payamtDate
                  ? convertDateThai(dayjs(dataDefault.payamtDate))
                  : "-"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="จำนวน">
              <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                {tableData ? `${tableData.length} รายการ` : "- "}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="จำนวนเงินรวม">
              <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                {currencyFormatPoint(totalAmount)} บาท
              </Text>
            </Descriptions.Item>
            {dataDefault?.approvalStatus === 0 && (
              <Descriptions.Item label="สถานะ">
                {renderStatus(dataDefault?.approvalStatus)}
              </Descriptions.Item>
            )}
            {dataDefault?.payamtMark && (
              <Descriptions.Item label="หมายเหตุเดิม" span={2}>
                <Text type="secondary">{dataDefault.payamtMark}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* ============== TABLE ============== */}
        {tableData && tableData.length > 0 ? (
          <>
            <Table
              className="premium-table"
              columns={columns}
              dataSource={tableData}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={loading}
              onRow={(record) => ({
                className: isLastInGroup(record, tableData)
                  ? "group-divider"
                  : "",
                style: {
                  backgroundColor: getGroupColor(
                    getGroupIndex(record, tableData),
                  ),
                },
              })}
              summary={() => {
                const total = tableData.reduce(
                  (sum, item) => sum + (parseFloat(item.NETPAY) || 0),
                  0,
                );

                return (
                  <Table.Summary>
                    <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                      <Table.Summary.Cell index={0} colSpan={3} align="right">
                        <Text strong style={{ fontSize: "16px" }}>
                          ยอดรวมทั้งหมด:
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text
                          strong
                          style={{ fontSize: "16px", color: "#1890ff" }}
                        >
                          {currencyFormatPoint(total)} ฿
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </>
        ) : (
          <Card size="small" style={{ textAlign: "center", padding: "20px" }}>
            <Text type="secondary">
              {loading ? "กำลังโหลดรายละเอียด..." : "ไม่มีรายการค่าใช้จ่าย"}
            </Text>
          </Card>
        )}
        <Form
          form={form}
          layout="vertical"
          style={{
            marginTop: 16,
            display: !dataDefault?.approvalStatus ? "block" : "none",
          }}
        >
          <Form.Item
            label={
              <span>
                <strong>หมายเหตุ</strong>
              </span>
            }
            name="remark"
          >
            <TextArea
              rows={5}
              placeholder="ระบุหมายเหตุ เช่น เหตุผลในการอนุมัติ/ไม่อนุมัติ"
            />
          </Form.Item>
        </Form>
        {/* ============== ADDITIONAL INFO ============== */}
        {dataDefault?.approvalStatus > 0 && (
          <Card
            size="small"
            style={{
              marginTop: 16,
              background: "#f0f5ff",
              border: "1px solid #adc6ff",
            }}
          >
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>ข้อมูลการอนุมัติ:</Text>
              <Descriptions span={3} size="small">
                <Descriptions.Item label="ผู้อนุมัติ">
                  {nameLawyers?.find(
                    (item) => item.id === dataDefault?.approvalBy,
                  )?.NNAME || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="วันที่อนุมัติ">
                  {dataDefault.approvalDate
                    ? convertDateThai(dayjs(dataDefault.approvalDate))
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="สถานะ">
                  {renderStatus(dataDefault?.approvalStatus)}
                </Descriptions.Item>
                {dataDefault.smPayMark && (
                  <Descriptions.Item label="หมายเหตุ" span={2}>
                    {dataDefault.smPayMark}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Space>
            <Space
              direction="vertical"
              size="small"
              style={{ width: "100%", marginTop: "5px" }}
            >
              <Text strong>ข้อมูลการตั้งเบิก:</Text>
              <Descriptions span={3} size="small">
                <Descriptions.Item label="เลขอ้างอิง">
                  {dataDefault?.DOCNO || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="วันที่ตั้ง">
                  {dataDefault.DOCDT
                    ? convertDateThai(dayjs(dataDefault.DOCDT))
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="สถานะ">
                  <Tag color={dataDefault?.DOCDT ? "green" : "red"}>
                    {" "}
                    {dataDefault?.DOCDT ? "ตั้งเบิกแล้ว" : "ยังไม่ตั้งเบิก"}
                  </Tag>
                </Descriptions.Item>
                {dataDefault.smPayMark && (
                  <Descriptions.Item label="หมายเหตุ" span={2}>
                    {dataDefault.smPayMark}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Space>
          </Card>
        )}
        {dataDefault?.approvalStatus === 1 &&
          bankData &&
          !dataDefault?.DOCDT &&
          handleModalCreate()}

        {/* ============== FOOTER BUTTONS ============== */}
        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {dataDefault?.approvalStatus === 0
              ? "* กรุณาตรวจสอบข้อมูลก่อนอนุมัติ"
              : dataDefault?.approvalStatus === 1 && !dataDefault?.DOCNO
                ? "* กรุณาตรวจสอบข้อมูลก่อนตั้งเบิก"
                : "* รายการนี้ดำเนินการแล้ว"}
          </Text>

          <Space size="middle">
            <Button size="large" danger onClick={handleClose}>
              {dataDefault?.approvalStatus === 0 ? "ยกเลิก" : "ปิด"}
            </Button>
            {(ROLE_ID === 1 || ROLE_ID === 6) &&
              dataDefault.approvalStatus === 1 && (
                <Popconfirm
                  title="ยืนยัน"
                  description="คุณต้องการบันทึกใช่หรือไม่ ?"
                  onConfirm={handleCreatePayment}
                  onCancel={null}
                  okText="ยืนยัน"
                  cancelText="ยกเลิก"
                >
                  <Button
                    size="large"
                    variant="outlined"
                    color="blue"
                    onClick={() => {
                      console.log("paymentType", paymentType);
                      console.log("selectedBank", selectedBank);
                    }}
                    disabled={
                      !paymentType ||
                      (paymentType !== "1" ? !selectedBank : null) ||
                      dataDefault.approvalStatus !== 1
                    }
                  >
                    ตั้งเบิก
                  </Button>
                </Popconfirm>
              )}
            {dataDefault?.approvalStatus === 0 && (
              <>
                <Button
                  size="large"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    form.validateFields().then((values) => {
                      handleReject(values);
                    });
                  }}
                  loading={loading}
                >
                  ไม่อนุมัติ
                </Button>

                <Button
                  size="large"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => {
                    form.validateFields().then((values) => {
                      handleApprove(values);
                    });
                  }}
                  loading={loading}
                >
                  อนุมัติ
                </Button>
              </>
            )}
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};

export default ExpenseApprovalModal;
